import toast from 'react-hot-toast';
import {
  EmailSettingsAggregate,
  Organization,
  OrganizationCsmSummary,
  OrgSendingSettings,
  DomainRecordStatus,
  DomainVerificationResult,
} from '../types/organization';
import { OrgSSOConfig, OrgSSOConfigUpdate } from '../types/sso';
import { apiClient } from './apiClient';
import { notifyEmailSchedulingStarted } from './emailSchedulingService';
import { captureHandledError } from '../utilities/datadog';

/** Wire format from POST .../verify endpoints (Pydantic field names). */
type DomainRecordStatusWire = Record<string, unknown>;
type DomainVerificationResultWire = {
  records: DomainRecordStatusWire[];
} & Record<string, unknown>;

/** Wire organization row from GET/POST/PATCH /organizations. */
type OrganizationApiRow = {
  id: string;
  name: string;
  sector?: string | null;
  fiscal_year_start?: string | null;
  crm?: string | null;
  cms?: string | null;
  csm_id?: string | number | null;
  csm?: OrganizationCsmSummary | null;
  domain?: string | null;
  major_gift_threshold?: number | null;
  average_major_gift?: number | null;
  average_legacy_gift?: number | null;
  icon_url?: string | null;
  logo_url?: string | null;
  aliases?: string[];
  TIN?: string | null;
  min_donor_age?: number | null;
  min_gift_amount?: number | null;
  demo_org?: boolean | null;
};

function formatOrganizationCsm(
  csm: OrganizationCsmSummary | null | undefined
): Organization['csm'] {
  if (!csm?.id) return csm === null ? null : undefined;
  return {
    id: String(csm.id),
    first_name: csm.first_name ?? null,
    last_name: csm.last_name ?? null,
    email: csm.email ?? null,
    image_url: csm.image_url ?? null,
  };
}

/** Normalize API organization wire data into app Organization shape. */
export function formatOrganization(org: OrganizationApiRow): Organization {
  return {
    id: org.id,
    name: org.name,
    sector: org.sector ?? '',
    fiscal_year_start: org.fiscal_year_start ?? '',
    crm: org.crm ?? '',
    cms: org.cms ?? null,
    csm_id: org.csm_id != null ? String(org.csm_id) : undefined,
    csm: formatOrganizationCsm(org.csm),
    domain: org.domain ?? '',
    major_gift_threshold: org.major_gift_threshold ?? null,
    average_major_gift: org.average_major_gift ?? null,
    average_legacy_gift: org.average_legacy_gift ?? null,
    icon_url: org.icon_url || null,
    logo_url: org.logo_url || null,
    aliases: org.aliases || [],
    TIN: org.TIN || undefined,
    min_donor_age: org.min_donor_age ?? null,
    min_gift_amount: org.min_gift_amount ?? null,
    isDemoOrg: org.demo_org ?? undefined,
  };
}

function normalizeDomainVerificationResult(
  raw: DomainVerificationResultWire
): DomainVerificationResult {
  return {
    isOverallOk: Boolean(raw['overall_ok']),
    records: raw.records.map(record => {
      const { ok, informational, help_links, ...rest } = record as {
        ok?: unknown;
        informational?: unknown;
        help_links?: DomainRecordStatus['help_links'];
      } & Omit<DomainRecordStatus, 'isOk' | 'isInformational' | 'help_links'>;
      return {
        ...rest,
        isOk: Boolean(ok),
        isInformational: Boolean(informational),
        help_links: help_links ?? [],
      };
    }),
  };
}

// Function to fetch all organizations
export const getOrganizations = async (): Promise<Organization[] | null> => {
  try {
    const response = await apiClient(`/organizations`, {
      method: 'GET',
    });
    if (response.ok) {
      const data = await response.json();
      if (!Array.isArray(data.organizations)) {
        return data.organizations;
      }
      return data.organizations.map(formatOrganization);
    } else {
      const body = await response.json();
      captureHandledError('Failed to fetch organizations', body);
      return null;
    }
  } catch (error) {
    captureHandledError('Error fetching organizations', error);
    return null;
  }
};

// Function to fetch a single organization by its ID
export const getOrganization = async (
  id: string
): Promise<Organization | null> => {
  try {
    const response = await apiClient(`/organizations?org_id=${id}`, {
      method: 'GET',
    });
    if (response.ok) {
      const data = await response.json();
      const row = data?.organizations?.[0];
      return row ? formatOrganization(row) : null;
    } else {
      const body = await response.json();
      captureHandledError('Failed to fetch organization', body, { id });
      return null;
    }
  } catch (error) {
    captureHandledError('Error fetching organization', error, { id });
    return null;
  }
};

// Function to create a single organization
export const createOrganization = async (
  payload: Record<string, unknown>
): Promise<Organization | null> => {
  try {
    const response = await apiClient('/organizations', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`Error creating an organization: ${response.statusText}`);
    }

    const raw = (await response.json()) as OrganizationApiRow;
    return formatOrganization(raw);
  } catch (error) {
    captureHandledError('Error creating an organization', error);
    return null;
  }
};
export type ApiOrgUpdate = Partial<{
  name: string | null;
  sector: string | null;
  fiscal_year_start: string | null;
  crm: string | null;
  cms: string | null;
  csm_id: string | null;
  domain: string | null;
  major_gift_threshold: number | null;
  average_major_gift: number | null;
  average_legacy_gift: number | null;
  min_donor_age: number | null;
  min_gift_amount: number | null;
  logo_file: File | null;
  icon_file: File | null;
  TIN: string | null;

}>;
export const updateOrganization = async (
  org_id: string,
  updates: ApiOrgUpdate
): Promise<Organization | null> => {
  // Create FormData for multipart form submission
  const formData = new FormData();
  // FastAPI Form collapses "" to omit.
  // For clearable string fields, send a space so the API can strip to None.
  // Omitting the key means "leave unchanged".
  const clearableFormKeys = new Set(['csm_id', 'cms']);
  Object.entries(updates).forEach(([key, value]) => {
    if (clearableFormKeys.has(key)) {
      if (value !== undefined) {
        formData.append(
          key,
          value === null || value === '' ? ' ' : String(value)
        );
      }
      return;
    }
    if (value !== null && value !== undefined) {
      if ((key === 'logo_file' || key === 'icon_file') && value instanceof File) {
        formData.append(key, value);
      } else if (key !== 'logo_file' && key !== 'icon_file') {
        formData.append(key, String(value));
      }
    }
  });

  const response = await apiClient(`/organizations?org_id=${org_id}`, {
    method: 'PATCH',
    // Don't set Content-Type header - let the browser set it with boundary for multipart
    body: formData,
  });

  if (!response.ok) {
    toast.error('Failed to update organization.');
    throw new Error(
      `Error updating organization: ${response.status} ${response.statusText}`
    );
  }

  const raw = (await response.json()) as OrganizationApiRow;
  return formatOrganization(raw);
};

export const getEmailSettings = async (
  org_id: string,
  effort_id?: string | null
): Promise<EmailSettingsAggregate | null> => {
  const params = new URLSearchParams({ org_id: org_id });
  if (effort_id) {
    params.append('effort_id', effort_id);
  }
  const response = await apiClient(`/org_email_settings?${params.toString()}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    toast.error('Failed to get organization policy.');
    throw new Error(
      `Error getting organization policy: ${response.status} ${response.statusText}`
    );
  }

  return response.json() as Promise<EmailSettingsAggregate>;
};

export const updateEmailSettings = async (
  org_id: string,
  payload: {
    org_policies_updates?: unknown[];
    no_send_updates?: unknown[];
    is_multiple_signers_enabled?: boolean;
  },
  effort_id?: string | null
): Promise<EmailSettingsAggregate | null> => {
  try {
    const params = new URLSearchParams({ org_id: org_id });
    if (effort_id) params.append('effort_id', effort_id);

    const response = await apiClient(
      `/org_email_settings?${params.toString()}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }
    );

    if (!response.ok) {
      const errBody = await response.text().catch(() => '');
      toast.error('Failed to update email settings.');
      throw new Error(
        `Error updating email settings: ${response.status} ${response.statusText} ${errBody}`
      );
    }

    toast.success('Email settings updated.');
    notifyEmailSchedulingStarted();
    return (await response.json()) as EmailSettingsAggregate;
  } catch (err) {
    captureHandledError('updateEmailSettings error', err);
    throw err;
  }
};

export const updateOrgPolicyEmergencyStop = async (
  org_id: string,
  stop_or_resume: boolean,
  effort_id?: string | null
): Promise<{ updated: number } | null> => {
  try {
    const params = new URLSearchParams({ org_id });
    if (effort_id) params.append('effort_id', effort_id);
    const response = await apiClient(
      `/org_policies_emergency_stop?${params.toString()}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(stop_or_resume),
        isErrorToastSuppressed: true,
      }
    );

    if (!response.ok) {
      const errBody = await response.text().catch(() => '');
      toast.error('Failed to update emergency stop for org policies.');
      throw new Error(
        `Error updating org policies emergency stop: ${response.status} ${response.statusText} ${errBody}`
      );
    }

    const data = (await response.json()) as { updated: number };
    toast.success('Organization policies updated.');
    notifyEmailSchedulingStarted();
    return data;
  } catch (err) {
    captureHandledError('updateOrgPolicyEmergencyStop error', err);
    throw err;
  }
};

export const getOrgDomainSettings = async (
  org_id: string
): Promise<OrgSendingSettings | null> => {
  try {
    const response = await apiClient(`/organizations/domain-settings?org_id=${org_id}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const body = await response.text().catch(() => '');
      toast.error('Failed to load domain settings.');
      throw new Error(
        `Error loading domain settings: ${response.status} ${response.statusText} ${body}`
      );
    }

    return (await response.json()) as OrgSendingSettings;
  } catch (err) {
    captureHandledError('getOrgDomainSettings error', err, { org_id });
    throw err;
  }
};

export type SaveMailgunDomainSettingsPayload = Partial<
  Pick<
    OrgSendingSettings,
    | 'sending_domain'
    | 'reply_to'
    | 'from_email'
    | 'from_email_name'
    | 'from_email_alternate_name'
    | 'email_signature'
  >
>;

export const saveMailgunDomainSettings = async (
  org_id: string,
  payload: SaveMailgunDomainSettingsPayload
): Promise<OrgSendingSettings> => {
  const response = await apiClient(`/organizations/mailgun-domain-settings?org_id=${org_id}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errBody = await response.text().catch(() => '');
    toast.error('Failed to save Mailgun domain settings.');
    throw new Error(
      `Error saving Mailgun domain settings: ${response.status} ${response.statusText} ${errBody}`
    );
  }

  const data = (await response.json()) as OrgSendingSettings;
  toast.success('Domain settings saved.');
  return data;
};

export const verifyMailgunDomainSettings = async (
  org_id: string
): Promise<DomainVerificationResult> => {
  const response = await apiClient(
    `/organizations/mailgun-domain-settings/verify?org_id=${org_id}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    }
  );

  if (!response.ok) {
    const errBody = await response.text().catch(() => '');
    toast.error('Failed to verify Mailgun domain settings.');
    throw new Error(
      `Error verifying Mailgun domain settings: ${response.status} ${response.statusText} ${errBody}`
    );
  }

  const raw = (await response.json()) as DomainVerificationResultWire;
  return normalizeDomainVerificationResult(raw);
};

export const saveSmtpDomainSettings = async (
  org_id: string,
  payload: Partial<
    Pick<
      OrgSendingSettings,
      | 'sending_domain'
      | 'reply_to'
      | 'from_email'
      | 'from_email_name'
      | 'from_email_alternate_name'
      | 'sending_settings'
    >
  >
): Promise<OrgSendingSettings> => {
  const response = await apiClient(`/organizations/smtp-domain-settings?org_id=${org_id}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errBody = await response.text().catch(() => '');
    toast.error('Failed to save SMTP domain settings.');
    throw new Error(
      `Error saving SMTP domain settings: ${response.status} ${response.statusText} ${errBody}`
    );
  }

  const data = (await response.json()) as OrgSendingSettings;
  toast.success('Domain settings saved.');
  return data;
};

export const verifySmtpDomainSettings = async (
  org_id: string,
  to_email?: string
): Promise<DomainVerificationResult> => {
  const response = await apiClient(
    `/organizations/smtp-domain-settings/verify?org_id=${org_id}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(to_email ? { to_email } : {}),
    }
  );

  if (!response.ok) {
    const errBody = await response.text().catch(() => '');
    toast.error('Failed to verify SMTP domain settings.');
    throw new Error(
      `Error verifying SMTP domain settings: ${response.status} ${response.statusText} ${errBody}`
    );
  }

  const raw = (await response.json()) as DomainVerificationResultWire;
  return normalizeDomainVerificationResult(raw);
};

export const getOrgSSOConfig = async (org_id: string): Promise<OrgSSOConfig> => {
  const response = await apiClient(`/organizations/sso?org_id=${org_id}`, {
    method: 'GET',
  });

  if (!response.ok) {
    toast.error('Failed to load SSO settings.');
    throw new Error(
      `Error loading SSO settings: ${response.status} ${response.statusText}`
    );
  }

  return response.json() as Promise<OrgSSOConfig>;
};

export const saveOrgSSOConfig = async (
  org_id: string,
  payload: OrgSSOConfigUpdate
): Promise<OrgSSOConfig> => {
  const response = await apiClient(`/organizations/sso?org_id=${org_id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    toast.error('Failed to save SSO settings.');
    throw new Error(
      `Error saving SSO settings: ${response.status} ${response.statusText}`
    );
  }

  const data = (await response.json()) as OrgSSOConfig;
  toast.success('SSO settings saved.');
  return data;
};
